import {
  Box,
  Grid,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { IconEdit, IconPlus, IconTrash } from '@tabler/icons'
import { format } from 'date-fns'
import { useState } from 'react'
import toast from 'react-hot-toast'
import useSWR from 'swr'

import { track, eventTypes } from 'torqcloud-ui'

import { useOrganization } from '@/contexts/OrganizationContext'
import type { SecretResponse } from '@api/generated/internal/internalApi'

import ApiKeyApi, { ApiKey } from '@/api/agentBuilder/apikey'
import useAgentApi from '@/api/hooks/useAgentApi'
import PageContainer from '@/components/layouts/PageContainer'
import PageTitle from '@/components/layouts/PageTitle'
import { StyledButton } from '@/components/ui/button/StyledButton'
import APIKeyDialog from '@/components/ui/dialog/APIKeyDialog'
import ConfirmDialog from '@/components/ui/dialog/ConfirmDialog'
import SecretDialog, { SecretDialogPropsType } from '@/components/ui/dialog/SecretDialog'
import useApiClient from '@/hooks/useApiClient'

import useConfirm from '@/hooks/useConfirm'
import APIEmptySVG from '@/images/api_empty.svg'

interface ConfirmPayload {
  title: string
  description: string
  confirmButtonName: string
  cancelButtonName: string
}

interface DialogProps {
  title: string
  type: 'ADD' | 'EDIT'
  cancelButtonName?: string
  confirmButtonName: string
  key?: ApiKey
}

// ==============================|| APIKeys ||============================== //

const APIKeys = () => {
  const [showDialog, setShowDialog] = useState<boolean>(false)
  const [dialogProps, setDialogProps] = useState<DialogProps>({} as DialogProps)

  const [showSecretDialog, setShowSecretDialog] = useState<boolean>(false)
  const [secretDialogProps, setSecretDialogProps] = useState<SecretDialogPropsType>({} as SecretDialogPropsType)

  const { confirm } = useConfirm()

  const { currentOrganizationId } = useOrganization()
  const apiClientContext = useApiClient()
  const apiKeyApi = new ApiKeyApi(apiClientContext.torqAgentClient)
  const agentApi = useAgentApi()

  const { data: apiKeys = [], mutate: mutateApiKeys } = useSWR(
    currentOrganizationId ? ['api-keys', currentOrganizationId] : null,
    async () => {
      const res = await apiKeyApi.getAllAPIKeys()
      return (res.data as ApiKey[]) ?? []
    }
  )

  const { data: secrets = [], mutate: mutateSecrets } = useSWR(
    currentOrganizationId ? ['secrets', currentOrganizationId] : null,
    async () => {
      const { data, error } = await agentApi.secrets.listSecrets()
      if (error) throw error
      return data ?? []
    },
    {
      onError: (error: unknown) => {
        const errorText = error instanceof Error ? error.message : String(error)
        toast.error(`Failed to fetch secrets: ${errorText}`)
      },
    }
  )

  const addNew = () => {
    const dialogProp: DialogProps = {
      title: 'Add New API Key',
      type: 'ADD',
      cancelButtonName: 'Cancel',
      confirmButtonName: 'Add',
    }
    setDialogProps(dialogProp)
    setShowDialog(true)
  }

  const addNewSecret = () => {
    const dialogProp: SecretDialogPropsType = {
      title: 'Create Secret',
      type: 'ADD',
      confirmButtonName: 'Create',
    }
    setSecretDialogProps(dialogProp)
    setShowSecretDialog(true)
  }

  const edit = (key: ApiKey) => {
    const dialogProp: DialogProps = {
      title: 'Edit API Key',
      type: 'EDIT',
      cancelButtonName: 'Cancel',
      confirmButtonName: 'Save',
      key,
    }
    setDialogProps(dialogProp)
    setShowDialog(true)
  }

  const editSecret = (secret: SecretResponse) => {
    const dialogProp: SecretDialogPropsType = {
      title: 'Edit Secret',
      type: 'EDIT',
      confirmButtonName: 'Save',
      secret,
    }
    setSecretDialogProps(dialogProp)
    setShowSecretDialog(true)
  }

  const deleteKey = async (key: ApiKey) => {
    const confirmPayload: ConfirmPayload = {
      title: `Delete`,
      description: `Api Access will be shut off for all agents that use this key. Delete key ${key.keyName}? `,
      confirmButtonName: 'Delete',
      cancelButtonName: 'Cancel',
    }
    const isConfirmed = await confirm(confirmPayload)

    if (isConfirmed) {
      try {
        const deleteResp = await apiKeyApi.deleteAPI(key.id)
        if (deleteResp.data) {
          toast.success('API key deleted')
          track(eventTypes.API_KEY_DELETED)
          onConfirm()
        }
      } catch (error: any) {
        const errorData = error.response?.data || `${error.response?.status}: ${error.response?.statusText}`
        toast.error(`Failed to delete API key: ${errorData}`)
        onCancel()
      }
    }
  }

  const deleteSecret = async (secret: SecretResponse) => {
    const confirmPayload: ConfirmPayload = {
      title: `Delete`,
      description: `Delete secret ${secret.name}?`,
      confirmButtonName: 'Delete',
      cancelButtonName: 'Cancel',
    }
    const isConfirmed = await confirm(confirmPayload)

    if (isConfirmed) {
      try {
        const { error } = await agentApi.secrets.deleteSecret(secret.id)
        if (error) throw error

        toast.success('Secret deleted')
        track(eventTypes.SECRET_DELETED)
        void mutateSecrets()
      } catch (error: unknown) {
        const errorText = error instanceof Error ? error.message : String(error)
        toast.error(`Failed to delete secret: ${errorText}`)
      }
    }
  }

  const onConfirm = () => {
    setShowDialog(false)
    void mutateApiKeys()
  }

  const onCancel = () => {
    setShowDialog(false)
  }

  const onConfirmSecret = () => {
    setShowSecretDialog(false)
    void mutateSecrets()
  }

  const onCancelSecret = () => {
    setShowSecretDialog(false)
  }

  return (
    <>
      <PageContainer>
        <PageTitle
          title="Keys & Secrets"
          actionComponent={() => (
            <Grid item xs="auto" sx={{ textAlign: 'right' }}>
              <StyledButton variant="contained" sx={{ textWrap: 'nowrap', px: '.75rem' }} onClick={addNew} startIcon={<IconPlus />}>
                Create Key
              </StyledButton>
            </Grid>
          )}
        />
        {apiKeys.length <= 0 && (
          <Stack sx={{ alignItems: 'center', justifyContent: 'center' }} flexDirection="column">
            <Box sx={{ p: 2, height: 'auto' }}>
              <img style={{ objectFit: 'cover', height: '30vh', width: 'auto' }} src={APIEmptySVG} alt="APIEmptySVG" />
            </Box>
            <div>No API Keys Yet</div>
          </Stack>
        )}
        {apiKeys.length > 0 && (
          <TableContainer component={Paper} sx={{ flexShrink: 0 }}>
            <Table sx={{ minWidth: 650 }} aria-label="simple table">
              <TableHead>
                <TableRow>
                  <TableCell>Key Name</TableCell>
                  <TableCell>API Key</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell> </TableCell>
                  <TableCell> </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {apiKeys.map((key, index) => (
                  <TableRow key={index} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    <TableCell component="th" scope="row">
                      {key.keyName}
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontFamily: 'monospace' }}>{key.apiKeyPrefix + '....'}</Typography>
                    </TableCell>
                    <TableCell>{format(new Date(key.createdDate), 'MM/dd/yyyy HH:MM:SS')}</TableCell>
                    <TableCell>
                      <IconButton title="Edit" color="primary" onClick={() => edit(key)}>
                        <IconEdit />
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <IconButton title="Delete" color="error" onClick={() => deleteKey(key)}>
                        <IconTrash />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Box sx={{ mt: 4, pb: 4, flexShrink: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h4">Secrets</Typography>
            <StyledButton variant="contained" sx={{ textWrap: 'nowrap', px: '.75rem' }} onClick={addNewSecret} startIcon={<IconPlus />}>
              Create Secret
            </StyledButton>
          </Box>

          {secrets.length <= 0 && (
            <Stack sx={{ alignItems: 'center', justifyContent: 'center' }} flexDirection="column">
              <div>No Secrets Yet</div>
            </Stack>
          )}

          {secrets.length > 0 && (
            <TableContainer component={Paper}>
              <Table sx={{ minWidth: 650 }} aria-label="secrets table">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Value</TableCell>
                    <TableCell> </TableCell>
                    <TableCell> </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {secrets.map((secret, index) => (
                    <TableRow key={index} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                      <TableCell component="th" scope="row">
                        {secret.name}
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontFamily: 'monospace' }}>{'••••••••'}</Typography>
                      </TableCell>
                      <TableCell>
                        <IconButton title="Edit" color="primary" onClick={() => editSecret(secret)}>
                          <IconEdit />
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <IconButton title="Delete" color="error" onClick={() => deleteSecret(secret)}>
                          <IconTrash />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </PageContainer>
      {showDialog && <APIKeyDialog show={showDialog} dialogProps={dialogProps} onCancel={onCancel} onConfirm={onConfirm}></APIKeyDialog>}
      {showSecretDialog && (
        <SecretDialog show={showSecretDialog} dialogProps={secretDialogProps} onCancel={onCancelSecret} onConfirm={onConfirmSecret} />
      )}
      <ConfirmDialog />
    </>
  )
}

export default APIKeys
